import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  keyframes,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  CardActions,
  TextareaAutosize,
  Alert,
} from "@mui/material";
import {
  Send as SendIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../context/AppContext";
import PageTransition from "../components/PageTransition";
import config from "../config"
interface Message {
  id: string;
  content: string;
  is_ai: boolean;
  created_at: string;
  report_json?: any;
  isTyping?: boolean;
  current_version?: number;
  has_versions?: boolean;
}

interface ReportVersion {
  id: string;
  version_number: number;
  report_json: any;
  remarks?: string;
  ai_response?: string;
  created_at: string;
}

interface Chat {
  id: string;
  title: string;
  status: "active" | "completed";
}

interface JobStatus {
  id: string;
  status: "pending" | "success" | "error";
  state: "system_analysis" | "report_generation" | "completed" | "error";
  content: string;
  report_json?: any;
}

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const jsonIconStyles = {
  width: "40px",
  height: "40px",
  cursor: "pointer",
  position: "relative",
  display: "inline-block",
  mt: 1,
  "& .json-icon": {
    width: "100%",
    height: "100%",
    backgroundImage: "url(/json_file.png)",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    transition: "all 0.3s ease",
  },
  "& .overlay": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    opacity: 0,
    "& .download-icon": {
      color: "white",
      transform: "translateY(-10px)",
      transition: "all 0.3s ease",
    },
  },
  "&:hover .overlay": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    opacity: 1,
    "& .download-icon": {
      transform: "translateY(0)",
    },
  },
} as const;

const DRAWER_WIDTH = 300;

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();
  const { token, user, logout } = useAuth();
  const { loading, setLoading } = useApp();
  const [messages, setMessages] = useState<Message[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [chat, setChat] = useState<Chat | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [loadingPhrase, setLoadingPhrase] = useState("");
  const loadingPhrasesRef = useRef<NodeJS.Timeout>();
  const [typingMessage, setTypingMessage] = useState<string>("");
  const [isTyping, setIsTyping] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState("");

  const systemAnalysisPhrases = [
    "Обдумываю, как лучше построить отчет...",
    "Анализирую требования к отчету...",
    "Проверяю, достаточно ли информации...",
    "Определяю необходимые параметры...",
    "Формирую структуру отчета...",
  ];

  const reportGenerationPhrases = [
    "Создаю отчет...",
    "Добавляю немного красоты...",
    "Настраиваю параметры...",
    "Генерирую SQL-запросы...",
    "Создаю интерфейс...",
  ];

  useEffect(() => {
    if (!chatId) {
      setOpenDialog(true);
    } else {
      fetchMessages();
      fetchChatDetails();
      fetchChats();
    }
  }, [chatId, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (loadingPhrasesRef.current) {
        clearInterval(loadingPhrasesRef.current);
        loadingPhrasesRef.current = undefined;
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChats = async () => {
    try {
      const response = await axios.get(config.baseBackURL + "/api/chats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChats(response.data);
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        config.baseBackURL + `/api/chats/${chatId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatDetails = async () => {
    try {
      const response = await axios.get(
        config.baseBackURL + `/api/chats/${chatId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setChat(response.data);
    } catch (error) {
      console.error("Error fetching chat details:", error);
    }
  };

  const handleCreateNewChat = async () => {
    try {
      const title = newChatTitle.trim() || "Новый чат";
      const response = await axios.post(
        config.baseBackURL + "/api/chats",
        { title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/chat/${response.data.id}`);
      fetchChats();
      setOpenDialog(false);
      setNewChatTitle("");
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  const handleCloseDialog = () => {
    if (!chatId) {
      navigate("/");
    }
    setOpenDialog(false);
    setNewChatTitle("");
  };

  const updateLoadingPhrase = (state: JobStatus["state"]) => {
    const phrases =
      state === "system_analysis"
        ? systemAnalysisPhrases
        : reportGenerationPhrases;

    if (loadingPhrasesRef.current) {
      clearInterval(loadingPhrasesRef.current);
    }

    setLoadingPhrase(phrases[0]);

    let phraseIndex = 0;
    loadingPhrasesRef.current = setInterval(() => {
      phraseIndex = (phraseIndex + 1) % phrases.length;
      setLoadingPhrase(phrases[phraseIndex]);
    }, 5000);
  };

  const typeMessage = async (message: Message) => {
    setIsTyping(true);
    let currentText = "";
    setTypingMessage("");

    for (let i = 0; i < message.content.length; i++) {
      currentText += message.content[i];
      setTypingMessage(currentText);
      await new Promise((resolve) => setTimeout(resolve, 10)); // Задержка 10мс между символами
    }

    setIsTyping(false);
    setTypingMessage("");
    setMessages((prev) => [...prev, { ...message, isTyping: false }]);
  };

  const checkJobStatus = async (jobId: string) => {
    try {
      const response = await axios.get(
        config.baseBackURL + `/api/chats/${chatId}/jobs/${jobId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const jobStatus: JobStatus = response.data;

      if (jobStatus.status === "pending") {
        updateLoadingPhrase(jobStatus.state);
        setTimeout(() => checkJobStatus(jobId), 2000);
      } else {
        setPendingJobId(null);
        setLoadingPhrase("");
        if (loadingPhrasesRef.current) {
          clearInterval(loadingPhrasesRef.current);
        }
        // Вместо прямого обновления сообщений, получаем их и анимируем последнее
        const messagesResponse = await axios.get(
          config.baseBackURL + `/api/chats/${chatId}/messages`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const newMessages = messagesResponse.data;
        if (newMessages.length > messages.length) {
          const lastMessage = newMessages[newMessages.length - 1];
          setMessages(newMessages.slice(0, -1));
          await typeMessage(lastMessage);
        }
      }
    } catch (error) {
      console.error("Error checking job status:", error);
      setPendingJobId(null);
      setLoadingPhrase("");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post(
        config.baseBackURL + `/api/chats/${chatId}/messages`,
        { content: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setMessages((prev) => [...prev, response.data.message]);
      setNewMessage("");

      if (response.data.jobId) {
        setPendingJobId(response.data.jobId);
        checkJobStatus(response.data.jobId);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (reportJson: any, version?: number) => {
    if (!chat?.title || !reportJson) return;
    
    const versionSuffix = version ? `_v${version}` : "";
    const blob = new Blob([JSON.stringify(reportJson, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${chat.title}${versionSuffix}.kisrep.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };


  return (
    <PageTransition>
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              borderRight: 1,
              borderColor: "divider",
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Новый чат
            </Button>
          </Box>

          <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
            {chats.map((chatItem) => (
              <ListItemButton
                key={chatItem.id}
                selected={chatItem.id === chatId}
                onClick={() => navigate(`/chat/${chatItem.id}`)}
                dense
              >
                <ListItemText
                  primary={chatItem.title}
                  secondary={
                    chatItem.status === "completed" ? "Завершен" : "Активный"
                  }
                  primaryTypographyProps={{ fontSize: "0.9rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8rem" }}
                />
              </ListItemButton>
            ))}
          </List>

          <Divider />
          <Box sx={{ p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                {user?.username?.[0].toUpperCase()}
              </Avatar>
              <Typography noWrap sx={{ flex: 1, fontSize: "0.9rem" }}>
                {user?.username}
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={logout}
            >
              Выйти
            </Button>
          </Box>
        </Drawer>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          }}
        >
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
              {chat?.title || "Новый чат"}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: "flex",
                  justifyContent: message.is_ai ? "flex-start" : "flex-end",
                }}
              >
                <Paper
                  sx={{
                    p: 2,
                    maxWidth: "70%",
                    backgroundColor: message.is_ai
                      ? "grey.100"
                      : "primary.light",
                    color: message.is_ai
                      ? "text.primary"
                      : "primary.contrastText",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {message.content}
                  </Typography>
                  {message.report_json && (
                    <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                      <Box
                        onClick={() => handleDownloadReport(message.report_json, message.current_version || 1)}
                        sx={jsonIconStyles}
                      >
                        <Box className="json-icon" />
                        <Box className="overlay">
                          <DownloadIcon className="download-icon" />
                        </Box>
                      </Box>
                      {message.has_versions && (
                        <Chip
                          label={`Версия ${message.current_version || 1}`}
                          size="small"
                          color="primary"
                          variant="filled"
                        />
                      )}
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1 }}
                  >
                    {new Date(message.created_at).toLocaleString()}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Paper
                  sx={{
                    p: 2,
                    maxWidth: "70%",
                    backgroundColor: "grey.100",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {typingMessage}
                  </Typography>
                </Paper>
              </Box>
            )}
            {pendingJobId && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  animation: `${fadeIn} 0.3s ease-out`,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    maxWidth: "70%",
                    background:
                      "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "1000px 100%",
                    animation: `${shimmer} 2s infinite linear`,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    boxShadow: "none",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                      animation: `${shimmer} 2s infinite linear`,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    {loadingPhrase}
                  </Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Введите сообщение... (Shift + Enter для новой строки)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={4}
                disabled={loading || !!pendingJobId}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSendMessage}
                disabled={loading || !newMessage.trim() || !!pendingJobId}
              >
                {loading ? <CircularProgress size={20} /> : <SendIcon />}
              </Button>
            </Box>
          </Box>
        </Box>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Новый чат</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название чата"
              fullWidth
              value={newChatTitle}
              onChange={(e) => setNewChatTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateNewChat();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button onClick={handleCreateNewChat} color="primary">
              Создать
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </PageTransition>
  );
};

export default Chat;
